/* Copyright (c) 2024 Xperi. All Rights Reserved. */

import { Diagnostics } from '@tivocorp/tivo-webapp-loggers';
import { SelectedMyShowsItem } from 'client-code/myshows';

import { MyshowFiltersError } from '../../core/error';
import { ServicesClientImpl } from '../../core/interactors';
import { Log } from '../../core/logging';
import { TMyShowsItemList } from '../../core/types';

type TMyShowsListResponse = {
    list: TMyShowsItemList;
    currentPage: number;
    networkCallId: number;
    hasError?: boolean;
    errorCode?: string;
};

export class MyShowsListGenerator {
    private TAG = 'MyShowsListGenerator';

    private static MAX_ITEM_IN_PAGE = 13;

    private static currentPage: number = 0;

    private static sizeOfList: number = 0;

    private static isFirstTime: boolean = true;

    private static isPartialList: boolean = false;

    private static isVisitedLastPage: boolean = false;

    private static lastSelectedIndex: number = 0;

    private static lastSelectedItemAbsoluteIndex: number = 0;

    private handleCommonError(
        _error: any,
        pageNumber: number,
        networkCallId: number,
    ): TMyShowsListResponse {
        const errorMessage = _error instanceof Error ? _error.message : JSON.stringify(_error);
        const myshowError = new MyshowFiltersError(_error?.code, _error?.message);
        Log.e(this.TAG, errorMessage);
        Diagnostics.error(this.TAG, errorMessage);
        return {
            list: {
                lastSelectedItem: {
                    index: 0,
                    myShowsItemId: '',
                },
                myShows: [],
                sortOrder: '',
                totalMyShowsCount: 0,
            },
            currentPage: pageNumber,
            networkCallId,
            hasError: true,
            errorCode: myshowError.getCode(),
        };
    }

    private static checkAndSetLastSelectedIndex(lastSelectedItem?: SelectedMyShowsItem): void {
        if (lastSelectedItem && lastSelectedItem?.index === -1) {
            let indexNumber = MyShowsListGenerator.lastSelectedIndex;
            if (MyShowsListGenerator.sizeOfList <= indexNumber)
                indexNumber = Math.max(MyShowsListGenerator.sizeOfList - 1, 0);
            // eslint-disable-next-line no-param-reassign
            lastSelectedItem.index = indexNumber;
        }
    }

    public async getList(networkCallId: number, filter?: string): Promise<TMyShowsListResponse> {
        if (MyShowsListGenerator.isFirstTime) {
            const service = new ServicesClientImpl();

            try {
                const res = await service.getMyShowsItemList({
                    myShowsFilter: filter,
                    offset: 0,
                    count: MyShowsListGenerator.MAX_ITEM_IN_PAGE,
                });
                const { lastSelectedItem, totalMyShowsCount: totalItems } = res;
                MyShowsListGenerator.sizeOfList = totalItems;
                MyShowsListGenerator.lastSelectedIndex =
                    lastSelectedItem?.index !== -1
                        ? (lastSelectedItem && lastSelectedItem?.index) || 0
                        : MyShowsListGenerator.lastSelectedItemAbsoluteIndex;
                MyShowsListGenerator.checkAndSetLastSelectedIndex(lastSelectedItem);
                MyShowsListGenerator.isFirstTime = false;
                MyShowsListGenerator.currentPage = Math.floor(
                    MyShowsListGenerator.lastSelectedIndex / MyShowsListGenerator.MAX_ITEM_IN_PAGE,
                );
                MyShowsListGenerator.isPartialList =
                    totalItems % MyShowsListGenerator.MAX_ITEM_IN_PAGE !== 0 &&
                    totalItems > MyShowsListGenerator.MAX_ITEM_IN_PAGE;

                // Get correct page based on last selected index
                if (MyShowsListGenerator.currentPage !== 0) {
                    return this.getList(networkCallId, filter);
                }
                return { list: res, currentPage: MyShowsListGenerator.currentPage, networkCallId };
            } catch (_error) {
                return this.handleCommonError(_error, 0, networkCallId);
            }
        }

        let updatedOffset: number | undefined;
        if (MyShowsListGenerator.isPartialList) {
            const lastPage = Math.floor(
                MyShowsListGenerator.sizeOfList / MyShowsListGenerator.MAX_ITEM_IN_PAGE,
            );

            if (
                MyShowsListGenerator.isVisitedLastPage ||
                MyShowsListGenerator.currentPage === lastPage
            ) {
                const numberOfElementInLastPage =
                    MyShowsListGenerator.sizeOfList % MyShowsListGenerator.MAX_ITEM_IN_PAGE;
                updatedOffset =
                    MyShowsListGenerator.MAX_ITEM_IN_PAGE * MyShowsListGenerator.currentPage -
                    (MyShowsListGenerator.MAX_ITEM_IN_PAGE - numberOfElementInLastPage);

                if (MyShowsListGenerator.currentPage === 0) {
                    updatedOffset = 0;
                    MyShowsListGenerator.isVisitedLastPage = false; // basically list resets
                } else if (MyShowsListGenerator.currentPage === lastPage) {
                    MyShowsListGenerator.isVisitedLastPage = true;
                }
            }
        }

        const service = new ServicesClientImpl();
        try {
            const res = await service.getMyShowsItemList({
                myShowsFilter: filter,
                offset:
                    updatedOffset ??
                    MyShowsListGenerator.currentPage * MyShowsListGenerator.MAX_ITEM_IN_PAGE,
                count: MyShowsListGenerator.MAX_ITEM_IN_PAGE,
            });
            const { lastSelectedItem } = res;
            MyShowsListGenerator.checkAndSetLastSelectedIndex(lastSelectedItem);
            return { list: res, currentPage: MyShowsListGenerator.currentPage, networkCallId };
        } catch (_error) {
            return this.handleCommonError(_error, MyShowsListGenerator.currentPage, networkCallId);
        }
    }

    public static setLastSelectedItemAbsoluteIndex(index: number): void {
        MyShowsListGenerator.lastSelectedItemAbsoluteIndex =
            MyShowsListGenerator.getAbsoluteIndex(index);
    }

    public static getAbsoluteIndex(index: number): number {
        // In case of Partial List
        if (MyShowsListGenerator.isVisitedLastPage && MyShowsListGenerator.currentPage !== 0)
            return this.sizeOfList - (this.MAX_ITEM_IN_PAGE - index);
        // In case of Complete lists
        return MyShowsListGenerator.currentPage * this.MAX_ITEM_IN_PAGE + index;
    }

    public static setCurrentPage(page: number): void {
        MyShowsListGenerator.currentPage = page;
    }

    public static isLastPageAndPartialList(): boolean {
        const lastPage = Math.floor(
            MyShowsListGenerator.sizeOfList / MyShowsListGenerator.MAX_ITEM_IN_PAGE,
        );
        return MyShowsListGenerator.isPartialList && MyShowsListGenerator.currentPage === lastPage;
    }

    public static isFirstPageAndPartialListWithVisitedLastPage(): boolean {
        return (
            MyShowsListGenerator.currentPage === 0 &&
            MyShowsListGenerator.isPartialList &&
            MyShowsListGenerator.isVisitedLastPage
        );
    }

    public static reset(): void {
        MyShowsListGenerator.currentPage = 0;
        MyShowsListGenerator.sizeOfList = 0;
        MyShowsListGenerator.isFirstTime = true;
        MyShowsListGenerator.isVisitedLastPage = false;
        MyShowsListGenerator.isPartialList = false;
    }
}
